import { Box, useTheme } from '@mui/material';

import { Switch, Text } from 'components';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { useTranslations } from 'hooks/useTranslations';

import { UseMutation, UseQuery } from 'useQuery';

interface OrganisationInvoicingActiveBarProps {
  organisationId: string;
}

// Slim page-level master toggle above the invoicing cards. Applies immediately
// (partial upsert of invoicing_active); the per-section Save buttons do not own
// this field.
function OrganisationInvoicingActiveBar({ organisationId }: OrganisationInvoicingActiveBarProps) {
  const theme = useTheme();
  const { hasMobileView } = useHasMobileView();
  const translations = useTranslations('organisationInvoicing');
  const { addToast } = useToast();

  const { data: contract } = UseQuery.invoicingContract({ id: organisationId });
  const { mutateAsync: updateContract, isLoading } = UseMutation.updateInvoicingContract();

  const active = !!contract?.invoicing_active;

  async function toggle(checked: boolean) {
    try {
      await updateContract({ id: organisationId, contract: { invoicing_active: checked } });
      addToast({ title: translations.contractSaved, severity: 'success' });
    } catch (error) {
      // handled by the mutation's onError toast
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        paddingX: 2,
        paddingY: 1.5,
        marginBottom: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
      }}
    >
      <Switch
        label={translations.active}
        checked={active}
        onChange={toggle}
        disabled={isLoading || contract === undefined}
        fullWidth
      />
      <Text.Small color="gray">{translations.activeHint}</Text.Small>
    </Box>
  );
}

export { OrganisationInvoicingActiveBar };
